import React, { useState } from 'react';
import './ModernHomePage.css';

const ModernHomePage = ({ userRole = 'customer', onNavigate }) => {
  const [selectedCard, setSelectedCard] = useState(null);

  // Sample data - replace with real API calls
  const stats = {
    bookings: { count: 24, change: '+12%', trend: 'up' },
    customers: { count: 156, change: '+8%', trend: 'up' },
    revenue: { count: '$12,450', change: '+23%', trend: 'up' },
    messages: { count: 89, change: '-4%', trend: 'down' },
  };

  const quickActions = [
    { id: 'new-booking', label: 'New Booking', icon: '📅', color: 'blue' },
    { id: 'new-contact', label: 'Add Contact', icon: '👥', color: 'green' },
    { id: 'send-campaign', label: 'Send Campaign', icon: '📢', color: 'purple' },
    { id: 'view-reports', label: 'View Reports', icon: '📈', color: 'orange' },
  ];

  const recentBookings = [
    {
      id: 1,
      customer: 'John Smith',
      time: 'Today at 2:00 PM',
      status: 'confirmed',
      service: 'Consultation',
    },
    {
      id: 2,
      customer: 'Sarah Johnson',
      time: 'Tomorrow at 10:00 AM',
      status: 'pending',
      service: 'Haircut',
    },
    {
      id: 3,
      customer: 'Mike Davis',
      time: 'Mar 5 at 3:30 PM',
      status: 'confirmed',
      service: 'Service Check',
    },
    {
      id: 4,
      customer: 'Emma Wilson',
      time: 'Mar 6 at 11:00 AM',
      status: 'confirmed',
      service: 'Appointment',
    },
  ];

  const upcomingEvents = [
    { id: 1, title: 'Team Meeting', time: 'Today at 4:00 PM', type: 'meeting' },
    { id: 2, title: 'Client Review', time: 'Tomorrow at 2:00 PM', type: 'review' },
    { id: 3, title: 'Report Deadline', time: 'Mar 8', type: 'deadline' },
  ];

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'new-booking':
        onNavigate('bookings');
        break;
      case 'new-contact':
        onNavigate('crm');
        break;
      case 'send-campaign':
        onNavigate('campaigns');
        break;
      case 'view-reports':
        onNavigate('analytics');
        break;
      default:
        break;
    }
  };

  return (
    <div className="modern-home-page">
      {/* Header */}
      <header className="home-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">Welcome back! 👋</h1>
            <p className="page-subtitle">Here's what's happening with your business today</p>
          </div>
          <div className="header-actions">
            <button className="btn-secondary">📅 Today</button>
            <button className="btn-secondary">⚙️ Settings</button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="home-main">
        {/* Stats Grid */}
        <section className="stats-section">
          <div className="stats-grid">
            {Object.entries(stats).map(([key, value]) => (
              <div key={key} className="stat-card">
                <div className="stat-header">
                  <span className="stat-label">
                    {key === 'bookings' && '📅 Bookings'}
                    {key === 'customers' && '👥 Customers'}
                    {key === 'revenue' && '💰 Revenue'}
                    {key === 'messages' && '💬 Messages'}
                  </span>
                  <span className={`stat-change ${value.trend}`}>
                    {value.change}
                  </span>
                </div>
                <div className="stat-value">{value.count}</div>
                <div className="stat-bar">
                  <div className="stat-fill" />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="quick-actions-section">
          <h2 className="section-title">Quick Actions</h2>
          <div className="quick-actions-grid">
            {quickActions.map((action) => (
              <button
                key={action.id}
                className={`quick-action-card ${action.color}`}
                onClick={() => handleQuickAction(action.id)}
              >
                <span className="action-icon">{action.icon}</span>
                <span className="action-label">{action.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Content Grid */}
        <div className="content-grid">
          {/* Upcoming Bookings */}
          <section className="card-section">
            <div className="section-header">
              <h2 className="section-title">Upcoming Bookings</h2>
              <button className="btn-text" onClick={() => onNavigate('bookings')}>
                View All →
              </button>
            </div>
            <div className="bookings-list">
              {recentBookings.map((booking) => (
                <div key={booking.id} className="booking-item">
                  <div className="booking-avatar">
                    {booking.customer.charAt(0)}
                  </div>
                  <div className="booking-details">
                    <div className="booking-name">{booking.customer}</div>
                    <div className="booking-service">{booking.service}</div>
                    <div className="booking-time">{booking.time}</div>
                  </div>
                  <div className={`booking-status ${booking.status}`}>
                    {booking.status}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Upcoming Events */}
          <section className="card-section">
            <div className="section-header">
              <h2 className="section-title">Upcoming Events</h2>
              <button className="btn-text">Add Event →</button>
            </div>
            <div className="events-list">
              {upcomingEvents.map((event) => (
                <div key={event.id} className={`event-item ${event.type}`}>
                  <div className="event-indicator" />
                  <div className="event-details">
                    <div className="event-title">{event.title}</div>
                    <div className="event-time">{event.time}</div>
                  </div>
                  <button className="event-action">→</button>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Chart Section */}
        <section className="chart-section">
          <div className="section-header">
            <h2 className="section-title">Performance Trend</h2>
            <div className="chart-controls">
              <button className="btn-small active">Week</button>
              <button className="btn-small">Month</button>
              <button className="btn-small">Year</button>
            </div>
          </div>
          <div className="chart-placeholder">
            <div className="mini-chart">
              <div className="chart-bar" style={{ height: '40%' }} />
              <div className="chart-bar" style={{ height: '65%' }} />
              <div className="chart-bar" style={{ height: '55%' }} />
              <div className="chart-bar" style={{ height: '80%' }} />
              <div className="chart-bar" style={{ height: '70%' }} />
              <div className="chart-bar" style={{ height: '85%' }} />
              <div className="chart-bar" style={{ height: '75%' }} />
            </div>
            <p className="chart-label">📈 Bookings per day (last 7 days)</p>
          </div>
        </section>

        {/* Resources */}
        <section className="resources-section">
          <h2 className="section-title">Resources & Guides</h2>
          <div className="resources-grid">
            <a href="#" className="resource-card">
              <span className="resource-icon">📚</span>
              <div className="resource-text">
                <div className="resource-title">Getting Started</div>
                <div className="resource-desc">Learn the basics</div>
              </div>
            </a>
            <a href="#" className="resource-card">
              <span className="resource-icon">🎥</span>
              <div className="resource-text">
                <div className="resource-title">Video Tutorials</div>
                <div className="resource-desc">Watch how-to videos</div>
              </div>
            </a>
            <a href="#" className="resource-card">
              <span className="resource-icon">💬</span>
              <div className="resource-text">
                <div className="resource-title">Contact Support</div>
                <div className="resource-desc">Get help from our team</div>
              </div>
            </a>
            <a href="#" className="resource-card">
              <span className="resource-icon">📖</span>
              <div className="resource-text">
                <div className="resource-title">Documentation</div>
                <div className="resource-desc">Read the docs</div>
              </div>
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ModernHomePage;
